import { useState } from 'react';
import { Box, TextField, Link, Alert } from '@mui/material';
import CustomButton from './CustomButton';

const LoginForm = ({ onSubmit, onForgotPassword, error, isGuest = false }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [validationErrors, setValidationErrors] = useState({
    email: '',
    password: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    
    // Clear validation error when user starts typing
    if (validationErrors[e.target.name]) {
      setValidationErrors({
        ...validationErrors,
        [e.target.name]: '',
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Password validation
    if (!formData.password.trim()) {
      errors.password = 'Password is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return; // Prevent submission if validation fails
    }
    
    onSubmit(formData);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Enter your email address"
          fullWidth
          required
          autoComplete="email"
          variant="standard"
          InputProps={{ disableUnderline: false }}
          error={!!validationErrors.email}
          helperText={validationErrors.email}
          aria-label="Email address"
          sx={{ 
            mb: 4,
            '& .MuiInputBase-input': {
              fontSize: '1.1rem',
              // Mobile responsive input
              '@media (max-width: 600px)': {
                fontSize: '1rem',
              },
            },
            '& .MuiFormHelperText-root': {
              fontSize: '0.875rem',
              '@media (max-width: 600px)': {
                fontSize: '0.8rem',
              },
            },
          }}
        />
        
        <TextField
          name="password"
          value={formData.password}
          onChange={handleChange}
          type="password"
          placeholder="Enter your password"
          fullWidth
          required
          autoComplete="current-password"
          variant="standard"
          InputProps={{ disableUnderline: false }}
          error={!!validationErrors.password}
          helperText={validationErrors.password}
          aria-label="Password"
          sx={{ 
            mb: 4,
            '& .MuiInputBase-input': {
              fontSize: '1.1rem',
              // Mobile responsive input
              '@media (max-width: 600px)': {
                fontSize: '1rem',
              },
            },
            '& .MuiFormHelperText-root': {
              fontSize: '0.875rem',
              '@media (max-width: 600px)': {
                fontSize: '0.8rem',
              },
            },
          }}
        />
        
        <CustomButton
          type="submit"
          text={isGuest ? "Enter as Guest" : "Login"}
          color="#121212"
          hoverColor="linear-gradient(90deg, #d5d5d5 0%, #ffffff 100%)"
          textColor="white"
          hoverTextColor="#000"
          width="100%"
          fullWidth
        />
        
        {!isGuest && (
          <Link
            component="button"
            type="button"
            underline="always"
            onClick={onForgotPassword}
            sx={{ 
              mt: 4, 
              display: 'block', 
              color: '#555', 
              fontSize: '1rem', 
              textAlign: 'center',
              width: '100%',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              // Mobile responsive link
              '@media (max-width: 600px)': {
                fontSize: '0.9rem',
                mt: 3,
              },
            }}
          >
            Forgot password?
          </Link>
        )}
      </Box>
    </Box>
  );
};

export default LoginForm; 